import { Component, computed, inject, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { params } from '@app/configurations';
import { AccountMoveBannerComponent } from '@app/core/components/account-move-banner/account-move-banner.component';
import { EmailVerificationComponent } from '@app/core/components/email-verification/email-verification.component';
import { SubscriptionAlertComponent } from '@app/core/components/subscription-alert/subscription-alert.component';
import { getDefaultLanguage } from '@app/core/config/locale.config';
import { UserLanguage } from '@app/core/models/user.models';
import { LoggingService } from '@app/core/services/error-logging.service';
import { GoogleTagManagerService } from '@app/core/services/google-tag-manager.service';
import { LocaleService } from '@app/core/services/locale.service';
import { StateService } from '@app/core/services/state.service';
import { AccountsState } from '@app/core/store/states/accounts/accounts.state';
import { CustomerState } from '@app/core/store/states/customer/customer.state';
import { RouterNavState } from '@app/core/store/states/router-nav/router-nav.state';
import { UserState } from '@app/core/store/states/user/user.state';
import { AppHasAuth } from '@app/core/utilities/app.utilities';
import { ImpersonationBannerComponent } from '@app/shared/components/impersonation-banner/impersonation-banner.component';
import { LayoutComponent } from '@app/shared/components/layout/layout.component';
import { SetHighChartsLocaleOptions } from '@app/shared/modules/charts/chart.utilities';
import { DisallowedOptimizationBannerComponent } from '@app/shared/modules/disallowed-optimization/components/disallowed-optimization-banner/disallowed-optimization-banner.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { select } from '@ngxs/store';
import * as Highcharts from 'highcharts';
import { NzAlertComponent } from 'ng-zorro-antd/alert';

@Component({
  templateUrl: './client.component.html',
  styleUrls: ['./client.component.scss'],
  imports: [
    LayoutComponent,
    NzAlertComponent,
    AccountMoveBannerComponent,
    SubscriptionAlertComponent,
    EmailVerificationComponent,
    ImpersonationBannerComponent,
    DisallowedOptimizationBannerComponent,
    RouterOutlet,
    TranslatePipe,
  ],
})
export class ClientComponent {
  private ss = inject(StateService);
  private ls = inject(LoggingService);
  private lcls = inject(LocaleService);
  readonly ts = inject(TranslateService);
  private gtm = inject(GoogleTagManagerService);

  readonly appHasAuth = signal(AppHasAuth());

  readonly user = select(UserState.user);
  readonly customer = select(CustomerState.customer);
  readonly customerWithAccounts = computed(() => this.customer());

  readonly hasAccounts = select(AccountsState.hasAccounts);
  readonly firstSyncPending = select(AccountsState.firstSyncPending);

  readonly isAdminPage = select(RouterNavState.dataParam('isAdminPage'));
  readonly impersonationId = select(RouterNavState.queryParam(params.IMPERSONATION_ID));

  readonly disableHeaderLinks = computed(() =>
    this.appHasAuth() ? !this.hasAccounts() || this.firstSyncPending() : true,
  );

  readonly inValidUser = computed(() => {
    const user = this.user();
    const customer = this.customer();
    return user === undefined || customer === undefined;
  });
  readonly language = computed(() => {
    return (
      this.user()?.lang?.toLowerCase() ||
      <UserLanguage>(
        (getDefaultLanguage(<UserLanguage>this.ts.defaultLang, <UserLanguage>this.lcls.defaultLanguage()) ||
          this.lcls.defaultLanguage())
      )
    );
  });

  constructor() {
    const user = this.user();
    const customer = this.customer();

    Highcharts.setOptions(SetHighChartsLocaleOptions(<UserLanguage>this.language().toUpperCase()));

    this.ss.setState();

    this.gtm.init(user, customer);
    this.ls.config(user);
  }
}
